import React from 'react';
import { motion } from 'framer-motion';
import { useAppSelector } from '../../store/hooks';

interface EnhancedLightCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    positive: boolean;
  };
  progress?: {
    percentage: number;
    show: boolean;
  };
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Horizontal compact layout: icon | label | value in one row — better for dashboard stat rows */
  compactHorizontal?: boolean;
}

const EnhancedLightCard: React.FC<EnhancedLightCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  progress,
  action,
  compactHorizontal = false,
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        delay: 0.5 + i * 0.2,
        ease: [0.25, 0.4, 0.25, 1] as const,
      },
    }),
  };

  // Compact horizontal layout: one row [icon | label | value] — reads as a clear metric strip
  if (compactHorizontal) {
    return (
      <div className={`relative w-full overflow-hidden rounded-lg border transition-all duration-300 ${
        theme === 'dark' 
          ? 'bg-gray-900/60 border-gray-700/60 hover:border-gray-600' 
          : 'bg-white/80 border-gray-200 hover:border-gray-300'
      }`}>
        <div className="relative z-10 flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
          {icon && (
            <div className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center ${
              theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
            }`}>
              {React.cloneElement(icon as React.ReactElement<any>, { className: 'h-4 w-4 sm:h-4 sm:w-4' })}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-medium truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {title}
            </p>
            <p className={`text-base sm:text-lg font-semibold tracking-tight truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {value}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative min-h-[48px] sm:min-h-[56px] lg:min-h-[64px] w-full overflow-hidden rounded-md sm:rounded-lg shadow border hover:shadow-md hover:scale-[1.01] transition-all duration-500 ease-out backdrop-blur-sm ${
      theme === 'dark' 
        ? 'bg-black/80 border-gray-800/50 hover:border-[#33FF00]/30' 
        : 'bg-white/90 border-gray-200/50 hover:border-[#33FF00]/20'
    }`}>
      {/* Premium background with subtle patterns */}
      <div className={`absolute inset-0 ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
          : 'bg-gradient-to-br from-gray-50 via-gray-100/80 to-gray-50'
      }`}></div>
      
      {/* Enhanced lighting effects */}
      <div className={`absolute inset-0 opacity-10 ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-[#33FF00]/15 via-transparent to-[#33FF00]/8' 
          : 'bg-gradient-to-br from-[#33FF00]/8 via-transparent to-[#33FF00]/5'
      }`}></div>
      
      {/* Content - centered in card */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center p-1.5 sm:p-2 min-h-[44px] sm:min-h-[52px] lg:min-h-[60px]">
        {icon && (
          <motion.div
            custom={0}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="flex justify-center mb-0.5"
          >
            <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded flex items-center justify-center shadow-sm transition-all duration-300 ${
              theme === 'dark' 
                ? 'bg-gradient-to-br from-gray-700 to-gray-800 ring-1 ring-gray-600/20 hover:ring-gray-500/40' 
                : 'bg-gradient-to-br from-gray-100 to-gray-200 ring-1 ring-gray-300/20 hover:ring-gray-400/40'
            }`}>
              <div className={`drop-shadow-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {React.cloneElement(icon as React.ReactElement<any>, { className: 'h-2.5 w-2.5 sm:h-3 sm:w-3' })}
              </div>
            </div>
          </motion.div>
        )}
        
        <div className="space-y-0 sm:space-y-0.5 flex flex-col items-center">
          <motion.h3 
            custom={1}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className={`text-[9px] sm:text-[10px] font-semibold tracking-wide leading-tight line-clamp-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
          >
            {title}
          </motion.h3>
          
          <motion.div 
            custom={2}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="flex items-baseline justify-center space-x-1"
          >
            <span className={`text-sm sm:text-base font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {value}
            </span>
          </motion.div>
          
          {subtitle && (
            <motion.p 
              custom={3}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
              className={`text-[9px] sm:text-[10px] font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
            >
              {subtitle}
            </motion.p>
          )}
    
          {/* Progress bar */}
          {progress?.show && (
            <motion.div 
              custom={4}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
              className="space-y-1 w-full max-w-[90%]"
            >
              <div className="flex justify-between items-center text-[9px] sm:text-[10px]">
                <span className={theme === 'dark' ? 'text-gray-300' : 'text-black'}>Progress</span>
                <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-black'}`}>
                  {Math.round(progress.percentage)}%
                </span>
              </div>
              <div className={`w-full rounded-full h-1.5 ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-200/50'} backdrop-blur-sm`}>
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(progress.percentage, 100)}%` }}
                  transition={{ duration: 1.5, delay: 1, ease: "easeOut" }}
                  className="bg-gradient-to-r from-[#33FF00] to-[#2BD400] h-1.5 rounded-full shadow-sm ring-1 ring-[#33FF00]/20"
                />
              </div>
            </motion.div>
          )}
          
          {action && (
            <motion.button
              custom={5}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
              onClick={action.onClick}
              className={`mt-1 text-[9px] sm:text-[10px] font-medium hover:text-[#2BD400] hover:underline focus:outline-none transition-colors ${
                theme === 'dark' ? 'text-gray-300' : 'text-black'
              }`}
            >
              {action.label} →
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedLightCard;
